import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { chatService } from '../services/chatService';

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 var(--brand-shadow); }
  70% { box-shadow: 0 0 0 12px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
`;

const dotBounce = keyframes`
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
`;

const ChatButton = styled.button`
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 100%);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 24px;
  z-index: 1001;
  box-shadow: 0 4px 15px var(--brand-shadow);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${pulse} 2s infinite;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px var(--brand-shadow-strong);
  }

  ${props => props.$isOpen && `
    animation: none;
    background: #666;
  `}
`;

const ChatWindow = styled.div`
  position: fixed;
  bottom: 100px;
  right: 30px;
  width: 380px;
  max-height: 520px;
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: 0 8px 40px var(--shadow-strong);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  opacity: ${props => props.$isOpen ? 1 : 0};
  transform: ${props => props.$isOpen ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)'};
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  transition: all 0.3s ease;

  @media (max-width: 480px) {
    width: calc(100vw - 20px);
    right: 10px;
    bottom: 80px;
    max-height: calc(100vh - 120px);
  }
`;

const ChatHeader = styled.div`
  background: linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 100%);
  color: white;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderAvatar = styled.div`
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const HeaderInfo = styled.div`
  flex: 1;

  h4 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  span {
    font-size: 12px;
    opacity: 0.85;
  }
`;

const AdminBadge = styled.span`
  background: rgba(255, 215, 0, 0.3);
  color: #ffd700;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 200px;
  max-height: 340px;

  @media (max-width: 480px) {
    max-height: calc(100vh - 280px);
  }
`;

const MessageBubble = styled.div`
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;

  ${props => props.$isUser ? `
    white-space: pre-wrap;
    align-self: flex-end;
    background: linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 100%);
    color: white;
    border-bottom-right-radius: 4px;
  ` : `
    align-self: flex-start;
    background: var(--chat-bubble-bg);
    color: var(--text-primary);
    border-bottom-left-radius: 4px;
  `}

  a {
    color: ${props => props.$isUser ? '#c8e6c9' : 'var(--brand-primary)'};
    text-decoration: underline;
  }

  /* Markdown content styling for assistant messages */
  p {
    margin: 0 0 8px 0;
    &:last-child { margin-bottom: 0; }
  }

  strong { font-weight: 600; }

  ul, ol {
    margin: 4px 0;
    padding-left: 20px;
  }

  li { margin: 2px 0; }

  table {
    width: 100%;
    border-collapse: collapse;
    margin: 8px 0;
    font-size: 13px;
  }

  th, td {
    border: 1px solid var(--border-light, #ddd);
    padding: 6px 10px;
    text-align: left;
  }

  th {
    background: var(--brand-light, #f5e8e0);
    font-weight: 600;
    color: var(--brand-primary, #c4704b);
    font-size: 12px;
  }

  tr:nth-child(even) td {
    background: var(--bg-elevated, #f9f9f9);
  }

  code {
    background: rgba(0, 0, 0, 0.06);
    padding: 1px 4px;
    border-radius: 3px;
    font-size: 13px;
  }
`;

const TypingIndicator = styled.div`
  align-self: flex-start;
  background: var(--chat-bubble-bg);
  padding: 12px 18px;
  border-radius: 16px;
  border-bottom-left-radius: 4px;
  display: flex;
  gap: 4px;
  align-items: center;
`;

const TypingDot = styled.span`
  width: 8px;
  height: 8px;
  background: var(--chat-typing-dot);
  border-radius: 50%;
  animation: ${dotBounce} 1.4s infinite ease-in-out both;
  animation-delay: ${props => props.$delay}s;
`;

const InputArea = styled.form`
  padding: 12px 16px;
  border-top: 1px solid var(--chat-input-border);
  display: flex;
  gap: 8px;
  align-items: center;
`;

const Input = styled.input`
  flex: 1;
  padding: 10px 14px;
  border: 1px solid var(--border-light);
  border-radius: 24px;
  font-size: 14px;
  outline: none;
  font-family: inherit;
  transition: border-color 0.2s;
  background: var(--bg-input);
  color: var(--text-primary);

  &:focus {
    border-color: var(--brand-secondary);
  }

  &::placeholder {
    color: var(--chat-placeholder);
  }
`;

const SendButton = styled.button`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-secondary) 100%);
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  transition: opacity 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const MessageTimestamp = styled.span`
  display: block;
  font-size: 10px;
  opacity: 0.6;
  margin-top: 4px;
  text-align: ${props => props.$isUser ? 'right' : 'left'};
`;

const ErrorMessage = styled.div`
  align-self: center;
  background: #fff3f3;
  color: #d32f2f;
  padding: 8px 14px;
  border-radius: 12px;
  font-size: 13px;
  text-align: center;
`;

const QuickActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 4px 0 8px;
  align-self: flex-start;
`;

const QuickActionButton = styled.button`
  background: var(--bg-primary);
  border: 1px solid var(--brand-secondary);
  color: var(--brand-primary);
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;

  &:hover {
    background: var(--brand-primary);
    color: var(--text-inverse);
  }
`;

const DataCard = styled.div`
  align-self: flex-start;
  max-width: 90%;
  background: var(--bg-elevated);
  border: 1px solid var(--chat-data-card-border);
  border-radius: 12px;
  overflow: hidden;
  font-size: 13px;
  margin-top: -4px;
`;

const DataCardHeader = styled.div`
  background: var(--chat-data-card-header-bg);
  padding: 8px 12px;
  font-weight: 600;
  color: var(--brand-primary);
  border-bottom: 1px solid var(--chat-data-card-border);
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const AvailabilityGroupLabel = styled.div`
  padding: 6px 12px 2px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${props => props.$available ? '#2e7d32' : '#888'};
  border-top: ${props => props.$first ? 'none' : '1px solid var(--chat-row-border)'};
`;

const RoomAvailabilityRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  border-bottom: 1px solid var(--chat-row-border);

  &:last-child {
    border-bottom: none;
  }
`;

const RoomCode = styled.span`
  font-family: monospace;
  font-weight: 700;
  font-size: 13px;
  color: var(--brand-primary);
  min-width: 36px;
  margin-right: 8px;
`;

const RoomName = styled.span`
  font-weight: 500;
  color: var(--text-primary);
  flex: 1;
`;

const StatusBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  white-space: nowrap;

  ${props => {
    switch (props.$status) {
      case 'available':
        return 'background: #e8f5e9; color: #2e7d32;';
      case 'booked':
        return 'background: #ffebee; color: #c62828;';
      case 'confirmed':
        return 'background: #e8f5e9; color: #2e7d32;';
      case 'pending':
        return 'background: #fff3e0; color: #e65100;';
      case 'cancelled':
        return 'background: #f5f5f5; color: #757575;';
      default:
        return 'background: #f5f5f5; color: #666;';
    }
  }}
`;

const BookingRow = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid var(--chat-row-border);
  display: flex;
  flex-direction: column;
  gap: 2px;

  &:last-child {
    border-bottom: none;
  }
`;

const BookingRowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const BookingCode = styled.span`
  font-family: monospace;
  font-weight: 600;
  color: var(--brand-primary);
  font-size: 12px;
`;

const BookingDetail = styled.span`
  color: var(--text-secondary);
  font-size: 12px;
`;

const GuestPickRow = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid var(--chat-data-card-border);
  transition: background 0.15s;

  &:hover {
    background: var(--brand-light);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const GuestPickInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const GuestPickArrow = styled.span`
  color: var(--text-muted);
  font-size: 18px;
  font-weight: 300;
  margin-left: 8px;
`;

const SuccessCard = styled.div`
  padding: 12px;
  text-align: center;
`;

const SuccessCode = styled.div`
  font-family: monospace;
  font-size: 18px;
  font-weight: 700;
  color: var(--brand-primary);
  margin: 8px 0;
  padding: 8px;
  background: var(--brand-light);
  border-radius: 8px;
`;

function StructuredDataCard({ data, language, onSendMessage }) {
  const isVi = language === 'vi';

  if (!data || !data.type) return null;

  switch (data.type) {
    case 'guestLookup': {
      if (!data.found || !data.multiple || !data.guests) return null;
      return (
        <DataCard>
          <DataCardHeader>
            {isVi ? `Tìm thấy ${data.matchCount} khách` : `Found ${data.matchCount} guest(s)`}
          </DataCardHeader>
          {data.guests.map((g, i) => (
            <GuestPickRow
              key={i}
              onClick={() => onSendMessage?.(`Chọn khách ${g.guestName}`)}
            >
              <GuestPickInfo>
                <span style={{ fontWeight: 600 }}>{g.guestName}</span>
                <BookingDetail>
                  {[g.phone, g.email].filter(Boolean).join(' · ') || (isVi ? 'Không có SĐT/email' : 'No phone/email')}
                </BookingDetail>
                {g.totalBookings > 0 && (
                  <BookingDetail>{g.totalBookings} {isVi ? 'lần đặt phòng' : 'booking(s)'}</BookingDetail>
                )}
              </GuestPickInfo>
              <GuestPickArrow>›</GuestPickArrow>
            </GuestPickRow>
          ))}
        </DataCard>
      );
    }

    case 'availability': {
      const rooms = data.rooms || [];
      const available = rooms.filter(r => r.available);
      const unavailable = rooms.filter(r => !r.available);
      return (
        <DataCard>
          <DataCardHeader>
            {isVi ? 'Tình trạng phòng' : 'Room Availability'}: {data.checkIn} → {data.checkOut}
          </DataCardHeader>
          {available.length > 0 && (
            <>
              <AvailabilityGroupLabel $available $first>
                {isVi ? `Còn trống (${available.length})` : `Available (${available.length})`}
              </AvailabilityGroupLabel>
              {available.map((room, i) => (
                <RoomAvailabilityRow key={`a-${i}`}>
                  {room.roomCode && <RoomCode>{room.roomCode}</RoomCode>}
                  <RoomName>
                    {isVi ? (room.name?.vi || room.name?.en || room.roomId) : (room.name?.en || room.name?.vi || room.roomId)}
                  </RoomName>
                  <StatusBadge $status="available">{isVi ? 'Trống' : 'Available'}</StatusBadge>
                </RoomAvailabilityRow>
              ))}
            </>
          )}
          {unavailable.length > 0 && (
            <>
              <AvailabilityGroupLabel $first={available.length === 0}>
                {isVi ? `Đã đặt (${unavailable.length})` : `Booked (${unavailable.length})`}
              </AvailabilityGroupLabel>
              {unavailable.map((room, i) => (
                <RoomAvailabilityRow key={`u-${i}`} style={{ opacity: 0.6 }}>
                  {room.roomCode && <RoomCode>{room.roomCode}</RoomCode>}
                  <RoomName>
                    {isVi ? (room.name?.vi || room.name?.en || room.roomId) : (room.name?.en || room.name?.vi || room.roomId)}
                  </RoomName>
                  <StatusBadge $status="booked">{isVi ? 'Đã đặt' : 'Booked'}</StatusBadge>
                </RoomAvailabilityRow>
              ))}
            </>
          )}
        </DataCard>
      );
    }

    case 'bookingSearch':
      return (
        <DataCard>
          <DataCardHeader>
            {isVi ? `Tìm thấy ${data.count} đặt phòng` : `Found ${data.count} booking(s)`}
          </DataCardHeader>
          {(data.bookings || []).map((b, i) => (
            <BookingRow key={i}>
              <BookingRowTop>
                <span style={{ fontWeight: 500 }}>{b.guestName}</span>
                <StatusBadge $status={b.status}>{b.status}</StatusBadge>
              </BookingRowTop>
              <BookingRowTop>
                <BookingCode>{b.bookingCode || '—'}</BookingCode>
                <BookingDetail>
                  {isVi ? (b.roomName?.vi || b.roomName?.en || b.roomId) : (b.roomName?.en || b.roomName?.vi || b.roomId)}
                </BookingDetail>
              </BookingRowTop>
              <BookingDetail>{b.checkIn} → {b.checkOut} | {b.phone}</BookingDetail>
            </BookingRow>
          ))}
        </DataCard>
      );

    case 'bookingLookup':
      if (!data.found) return null;
      const b = data.booking;
      return (
        <DataCard>
          <DataCardHeader>{isVi ? 'Thông tin đặt phòng' : 'Booking Details'}</DataCardHeader>
          <BookingRow>
            <BookingRowTop>
              <BookingCode>{b.bookingCode}</BookingCode>
              <StatusBadge $status={b.status}>{b.status}</StatusBadge>
            </BookingRowTop>
            <BookingDetail style={{ fontWeight: 500, color: '#333' }}>{b.guestName}</BookingDetail>
            <BookingDetail>
              {isVi ? (b.roomName?.vi || b.roomName?.en || b.roomId) : (b.roomName?.en || b.roomName?.vi || b.roomId)}
            </BookingDetail>
            <BookingDetail>{b.checkIn} → {b.checkOut} | {b.guests} {isVi ? 'khách' : 'guests'}</BookingDetail>
          </BookingRow>
        </DataCard>
      );

    case 'bookingCreated':
      return (
        <DataCard>
          <DataCardHeader>{isVi ? 'Đặt phòng thành công' : 'Booking Created'}</DataCardHeader>
          <SuccessCard>
            <div style={{ color: '#2e7d32', fontSize: 24 }}>&#10003;</div>
            <SuccessCode>{data.bookingCode}</SuccessCode>
            <BookingDetail>{data.guestName}</BookingDetail>
            <BookingDetail>
              {isVi ? (data.roomName?.vi || data.roomName?.en) : (data.roomName?.en || data.roomName?.vi)} | {data.checkIn} → {data.checkOut}
            </BookingDetail>
          </SuccessCard>
        </DataCard>
      );

    default:
      return null;
  }
}

function ChatWidget() {
  const { t, currentLanguage } = useLanguage();
  const { isAdmin: admin } = useAuth();
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  const handleToggle = () => {
    const opening = !isOpen;
    setIsOpen(opening);

    if (opening && !hasOpened) {
      setHasOpened(true);
      const welcomeContent = admin
        ? t(
          'Welcome to Hoang Uyen Da Lat Homestay!\nI can help you manage bookings, check availability, or search bookings. What would you like to do?',
          'Chào mừng đến với\nHoàng Uyên Đà Lạt Homestay!\nTôi có thể giúp bạn quản lý đặt phòng, kiểm tra phòng trống, hoặc tìm kiếm đặt phòng. Bạn cần gì?'
        )
        : t(
          'Welcome to Hoang Uyen Da Lat Homestay!\nI can help you check room availability, look up your booking, or answer questions about our rooms. How can I help you?',
          'Chào mừng đến với\nHoàng Uyên Đà Lạt Homestay!\nTôi có thể giúp bạn kiểm tra phòng trống, tra cứu đặt phòng, hoặc trả lời câu hỏi về các phòng. Tôi có thể giúp gì cho bạn?'
        );
      setMessages([{
        role: 'assistant',
        content: welcomeContent,
        showQuickActions: true
      }]);
    }
  };

  const handleQuickAction = (text) => {
    if (isLoading) return;
    setInput(text);
    // Submit via a synthetic form event
    handleSend(text);
  };

  // Detect contextual quick actions based on the last assistant message
  const getContextualActions = (msg) => {
    if (!msg || msg.role !== 'assistant') return null;
    const content = (msg.content || '').toLowerCase();
    const isVi = currentLanguage === 'vi';
    const sd = msg.structuredData;

    // After booking created successfully
    if (sd?.type === 'bookingCreated') {
      return [
        { label: isVi ? 'Đặt phòng mới' : 'New booking', text: isVi ? 'Tôi muốn tạo thêm đặt phòng' : 'I want to create another booking' },
        { label: isVi ? 'Xem đặt phòng' : 'View bookings', text: isVi ? 'Tìm đặt phòng hôm nay' : 'Search bookings for today' },
      ];
    }

    // After checkout
    if (content.includes('checked out') || content.includes('trả phòng thành công') || content.includes('check-out thành công')) {
      return [
        { label: isVi ? 'Trả phòng khác' : 'Checkout another', text: isVi ? 'Tôi muốn trả phòng cho khách khác' : 'I want to checkout another guest' },
        { label: isVi ? 'Đặt phòng mới' : 'New booking', text: isVi ? 'Tôi muốn tạo nhanh đặt phòng' : 'I want to quick-create a booking' },
      ];
    }

    // Confirmation prompt (bot asking to confirm a booking/action)
    if (content.includes('xác nhận') || content.includes('confirm')) {
      return [
        { label: isVi ? 'Xác nhận' : 'Confirm', text: isVi ? 'Xác nhận' : 'Confirm' },
        { label: isVi ? 'Hủy' : 'Cancel', text: isVi ? 'Hủy, không tạo' : 'Cancel, don\'t create' },
      ];
    }

    // Bot asks yes/no question (e.g. "Bạn có muốn đặt phòng?", "Do you want to book?")
    if (content.includes('bạn có muốn') || content.includes('do you want to')) {
      return [
        { label: isVi ? 'Có' : 'Yes', text: isVi ? 'Có' : 'Yes' },
        { label: isVi ? 'Không' : 'No', text: isVi ? 'Không' : 'No' },
      ];
    }

    // After availability check (admin)
    if (sd?.type === 'availability' && admin && isAdminPage) {
      return [
        { label: isVi ? 'Đặt phòng' : 'Book room', text: isVi ? 'Tôi muốn tạo nhanh đặt phòng' : 'I want to quick-create a booking' },
      ];
    }

    // After booking search results
    if (sd?.type === 'bookingSearch') {
      return [
        { label: isVi ? 'Tìm thêm' : 'Search more', text: isVi ? 'Tìm đặt phòng khác' : 'Search for other bookings' },
        { label: isVi ? 'Đặt phòng mới' : 'New booking', text: isVi ? 'Tôi muốn tạo nhanh đặt phòng' : 'I want to quick-create a booking' },
      ];
    }

    // After booking lookup
    if (sd?.type === 'bookingLookup') {
      return [
        { label: isVi ? 'Tra cứu khác' : 'Lookup another', text: isVi ? 'Tôi muốn tra cứu đặt phòng khác' : 'I want to look up another booking' },
      ];
    }

    return null;
  };

  const handleSend = async (messageText) => {
    const trimmed = (messageText || '').trim();
    if (!trimmed || isLoading) return;

    const userMessage = { role: 'user', content: trimmed, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    const history = messages.map(m => ({ role: m.role, content: m.content }));

    const result = await chatService.sendMessage(trimmed, history, currentLanguage, admin);

    if (result.success) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: result.reply,
        structuredData: result.structuredData || null,
        timestamp: new Date()
      }]);
    } else {
      setMessages(prev => [...prev, { role: 'error', content: result.error }]);
    }

    setIsLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    handleSend(input);
  };

  const guestQuickActions = [
    { label: t('Room availability', 'Phòng trống'), text: t('What rooms are available?', 'Phòng nào còn trống?') },
    { label: t('Check booking', 'Tra cứu đặt phòng'), text: t('I want to check my booking', 'Tôi muốn tra cứu đặt phòng') },
    { label: t('Room info', 'Thông tin phòng'), text: t('Tell me about your rooms', 'Cho tôi biết về các phòng') }
  ];

  const adminQuickActions = [
    { label: t('Quick booking', 'Đặt phòng ngay'), text: t('I want to quick-create a booking', 'Tôi muốn tạo nhanh đặt phòng') },
    { label: t('Checkout', 'Trả phòng'), text: t('I want to checkout a guest', 'Tôi muốn trả phòng cho khách') }
  ];

  const quickActions = (admin && isAdminPage) ? adminQuickActions : guestQuickActions;

  return (
    <>
      <ChatWindow $isOpen={isOpen} role="dialog" aria-label={t('Chat window', 'Cửa sổ chat')}>
        <ChatHeader>
          <HeaderAvatar>🏡</HeaderAvatar>
          <HeaderInfo>
            <h4>
              {t('Hoang Uyen Assistant', 'Trợ lý Hoàng Uyên')}
              {admin && <AdminBadge>Admin</AdminBadge>}
            </h4>
            <span>{t('Online - Ask me anything!', 'Trực tuyến - Hãy hỏi tôi!')}</span>
          </HeaderInfo>
        </ChatHeader>

        <MessagesContainer>
          {messages.map((msg, i) => {
            if (msg.role === 'error') {
              return <ErrorMessage key={i}>{msg.content}</ErrorMessage>;
            }
            const isLastMessage = i === messages.length - 1;
            const contextualActions = isLastMessage && !isLoading ? getContextualActions(msg) : null;
            return (
              <React.Fragment key={i}>
                <MessageBubble $isUser={msg.role === 'user'}>
                  {msg.role === 'user' ? msg.content : (
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {msg.content}
                    </ReactMarkdown>
                  )}
                  {msg.timestamp && (
                    <MessageTimestamp $isUser={msg.role === 'user'}>
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </MessageTimestamp>
                  )}
                </MessageBubble>
                {msg.structuredData && (
                  <StructuredDataCard data={msg.structuredData} language={currentLanguage} onSendMessage={handleSend} />
                )}
                {msg.showQuickActions && (
                  <QuickActions>
                    {quickActions.map((action, j) => (
                      <QuickActionButton
                        key={j}
                        onClick={() => handleQuickAction(action.text)}
                        disabled={isLoading}
                      >
                        {action.label}
                      </QuickActionButton>
                    ))}
                  </QuickActions>
                )}
                {contextualActions && (
                  <QuickActions>
                    {contextualActions.map((action, j) => (
                      <QuickActionButton
                        key={`ctx-${j}`}
                        onClick={() => handleQuickAction(action.text)}
                        disabled={isLoading}
                      >
                        {action.label}
                      </QuickActionButton>
                    ))}
                  </QuickActions>
                )}
              </React.Fragment>
            );
          })}
          {isLoading && (
            <TypingIndicator>
              <TypingDot $delay={0} />
              <TypingDot $delay={0.2} />
              <TypingDot $delay={0.4} />
            </TypingIndicator>
          )}
          <div ref={messagesEndRef} />
        </MessagesContainer>

        <InputArea onSubmit={handleSubmit}>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('Type your message...', 'Nhập tin nhắn...')}
            disabled={isLoading}
          />
          <SendButton type="submit" disabled={isLoading || !input.trim()} aria-label={t('Send message', 'Gửi tin nhắn')}>
            <i className="fas fa-paper-plane" />
          </SendButton>
        </InputArea>
      </ChatWindow>

      <ChatButton
        $isOpen={isOpen}
        onClick={handleToggle}
        aria-label={t('Chat with us', 'Chat với chúng tôi')}
      >
        <i className={isOpen ? 'fas fa-times' : 'fas fa-comment-dots'} />
      </ChatButton>
    </>
  );
}

export default ChatWidget;
